import { useState } from 'react'

import { Line } from '../../models/Line'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    line?: Line
    onClose: () => void
    onSave: (l: Line) => void
}

export function NewChargeCustomLineModal({ line, onClose, onSave }: Props) {
    const [editedLine, setEditedLine] = useState(line)

    return (
        <Modal
            title="Edit item"
            isOpen={!!line}
            onClose={onClose}
            footer={
                <Button
                    intent="primary"
                    onClick={() => {
                        if (editedLine) {
                            onSave(editedLine)
                        }
                    }}
                >
                    Done
                </Button>
            }
        >
            <div className="flex flex-col gap-8">
                <WorkflowPage.IconSection icon="dollar.large" title="Amount">
                    <CurrencyField
                        size="medium"
                        className="border-gray-500"
                        isFullWidth
                        value={editedLine?.rate}
                        onChange={(e) => {
                            const newLine = editedLine?.clone({
                                rate: e,
                                total: e ? e * (editedLine.quantity ?? 0) : undefined,
                            })
                            setEditedLine(newLine)
                        }}
                    />
                </WorkflowPage.IconSection>
                <WorkflowPage.IconSection icon="hash.large" title="Quantity">
                    <TextField
                        size="medium"
                        className="border-gray-500"
                        isFullWidth
                        type="number"
                        min={1}
                        value={editedLine?.quantity}
                        onChange={(e) => {
                            const qty = Number(e.target.value)
                            const rate = editedLine?.rate ?? 0
                            const newLine = editedLine?.clone({
                                quantity: qty,
                                total: rate * qty,
                            })
                            setEditedLine(newLine)
                        }}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection icon="note.large" title="Description">
                    <TextArea
                        className="border-gray-500"
                        isAutoResizing
                        value={editedLine?.memo}
                        placeholder="Enter description"
                        onClear={() => setEditedLine((l) => l?.clone({ memo: '' }))}
                        onChange={(e) => setEditedLine((l) => l?.clone({ memo: e.target.value }))}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    icon="percentage2.large"
                    title="Allow tax"
                    hasHoverEffect
                    secondaryView={
                        <Checkbox
                            isChecked={editedLine?.taxable}
                            onChange={() =>
                                setEditedLine((l) => l?.clone({ taxable: !l?.taxable }))
                            }
                        />
                    }
                />

                <WorkflowPage.IconSection
                    icon="discount.large"
                    title="Allow discount"
                    hasHoverEffect
                    secondaryView={
                        <Checkbox
                            isChecked={editedLine?.discountable}
                            onChange={() =>
                                setEditedLine((l) => l?.clone({ discountable: !l?.discountable }))
                            }
                        />
                    }
                />
            </div>
        </Modal>
    )
}
